import { useQuery } from '@apollo/client';
import { Schedule } from '@mui/icons-material';
import { Box } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useDeleteScheduledShow, useUpdateScheduledShow } from '~/api/scheduled-show';
import { ListPagination } from '~/components/ListPagination';
import {
  ListActions,
  ListContainer,
  ListContent,
  ListHeader,
  ListPageContainer,
} from '~/components/PageLayout';
import { Count } from '~/components/count';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { SearchBar } from '~/components/forms/search-bar';
import { PastSchedulesSwitch } from '~/components/schedule/past-switch';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import type { ScheduleInput, ScheduledShowsOrderBy } from '~/generated/graphql';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useOrderByParams } from '~/hooks/order-by';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import { assert } from '~/lib/assert';
import {
  ScheduledShowsListDocument,
  type ScheduledShows__ScheduledShowList as ScheduledShow,
} from '../queries/queries.generated';
import { BulkActions } from './components/bulk-actions';
import { Table } from './components/table';

export const ScheduleList = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const [currentScheduledShow, setCurrentScheduledShow] = useState<ScheduledShow>();

  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';
  const includeExpired = searchParams.get('expired')?.trim().toLowerCase() === 'true';

  const { orderBy } = useOrderByParams<ScheduledShowsOrderBy>();
  const { handleOutOfBounds, page, perPage } = usePaginationParams();

  const { data, loading } = useQuery(ScheduledShowsListDocument, {
    variables: {
      networkId: currentNetwork.id,
      page,
      perPage,
      search,
      includeExpired,
      ...(orderBy && { orderBy }),
    },
    onCompleted: (data) => handleOutOfBounds(data.network?.scheduledShows.pageInfo),
  });

  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);

  const handleSwitchChange = (showPast: boolean) => {
    setSearchParams((x) => {
      x.set('expired', showPast.toString());
      return x;
    });
  };

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();
  const [deleteSchedule] = useDeleteScheduledShow();
  const [updateSchedule] = useUpdateScheduledShow();

  const deleteScheduledShow = async (scheduledShow: ScheduledShow) => {
    setCurrentScheduledShow(scheduledShow);
    if (!(await confirmDelete())) return;
    void deleteSchedule({ variables: { scheduledShowIds: [scheduledShow.id] } });
  };

  const deleteScheduledShowBulk = async () => {
    if (!(await confirmDelete())) return;
    void deleteSchedule({ variables: { scheduledShowIds: selectedIds.map((x) => +x) } });
    setSelectedIds([]);
  };

  const updateScheduledShow = async (
    name: string | null,
    schedule: ScheduleInput,
    showId: number,
    scheduledShowId?: number,
  ) => {
    assert(scheduledShowId != null, 'no scheduled show');
    await updateSchedule({
      variables: {
        id: scheduledShowId,
        patch: { name, showId, schedule },
      },
    });
  };

  const totalCount = useTotalCount(data?.network?.scheduledShows.pageInfo.nodesCount);

  return (
    <>
      <Helmet title={t('pages.schedules.title')} />
      <Toolbar titleIcon={<Schedule />} titleText={t('pages.schedules.title')}></Toolbar>
      <ListPageContainer>
        <ListContainer>
          <ListHeader>
            <ListActions>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SearchBar placeholder={t('pages.schedules.search_placeholder')} search={search} />
                <PastSchedulesSwitch
                  onSwitchChange={handleSwitchChange}
                  includeExpired={includeExpired}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {selectedIds.length > 0 ? <BulkActions onDelete={deleteScheduledShowBulk} /> : null}
                <Count
                  selectedCount={selectedIds.length}
                  totalCount={totalCount}
                  thing="pages.schedules.thing_plural"
                />
              </Box>
            </ListActions>
          </ListHeader>
          <ListContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <Table
                  loading={loading}
                  onCheck={setSelectedIds}
                  onDelete={deleteScheduledShow}
                  onSubmit={updateScheduledShow}
                  scheduledShows={data?.network?.scheduledShows.nodes ?? []}
                  selectedIds={selectedIds}
                />
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ListPagination totalCount={totalCount} />
              </Box>
            </Box>
          </ListContent>
        </ListContainer>
      </ListPageContainer>
      <ConfirmDialog
        {...confirmDeleteProps}
        confirm={t('common.dialogs.delete')}
        cancel={t('common.dialogs.cancel')}
        deleteConfirm
        prompt={
          <Trans
            i18nKey="pages.schedules.list.delete_dialog.prompt"
            values={{
              device_count: currentScheduledShow?.deviceCount,
              devices: t('common.pluralize.device_plural', {
                count: currentScheduledShow?.deviceCount ?? 0,
              }),
            }}
            components={{ b: <b /> }}
          />
        }
        title={t('pages.schedules.list.delete_dialog.title')}
      />
    </>
  );
};
